import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Server, Lock, Users, Eye, Bell, RefreshCw, Building2, Globe, FileText, CheckCircle2 } from 'lucide-react';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';

const Section = ({ icon: Icon, title, children }) => (
  <section>
    <h2 className="text-xl md:text-2xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
      <Icon className="h-5 w-5 text-[#1e3a5f] shrink-0" />
      {title}
    </h2>
    {children}
  </section>
);

const SecurityCompliance = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="security-compliance-page">
      <SEO title="Security & Compliance | StayMii" description="StayMii platform security practices, cloud infrastructure, encryption, access control, and LHDN MyInvois compliance." canonicalUrl="/security" />
      <LandingNavBar />

      <div className="bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] pt-28 pb-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Shield className="h-10 w-10 text-white mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" data-testid="security-title">Security & Compliance</h1>
          <p className="text-white/70 text-sm max-w-2xl mx-auto">Security, data protection, and regulatory compliance are core principles in the design and operation of the StayMii platform.</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 md:p-8 space-y-8">

          <Section icon={Globe} title="Platform Overview">
            <div className="bg-slate-50 rounded-lg p-4 text-slate-600 text-sm mb-4">
              <p className="font-semibold text-slate-800">Operated by Mii Global Partner Holdings Sdn Bhd</p>
              <p>Registration No: 202601028203</p>
              <p>11, Taman Bukit Dumbar, 11600 Jelutong, Penang, Malaysia</p>
            </div>
            <p className="text-slate-600 leading-relaxed mb-4">The StayMii platform consists of three core modules:</p>
            <div className="space-y-3">
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-800 mb-2">StayMii Lite</h3>
                <p className="text-slate-600 text-sm mb-2">A cloud-based property management system for hospitality operators to manage:</p>
                <ul className="list-disc list-inside text-slate-600 text-sm space-y-1 ml-2">
                  <li>Reservations, bookings, and guest information</li>
                  <li>Billing, folios, and housekeeping operations</li>
                  <li>OTA integrations and operational reporting</li>
                </ul>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-800 mb-2">StayMii e-Invoice</h3>
                <p className="text-slate-600 text-sm">Designed to help businesses generate and manage electronic invoices in accordance with the Malaysian e-Invoice framework administered by LHDN through the MyInvois system.</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-800 mb-2">StayMii e-Ticket</h3>
                <p className="text-slate-600 text-sm">A ticketing and OTA distribution module for attractions, tours, and event operators. Issues QR-coded tickets, integrates with connected OTA channel partners, and provides on-site scanning validation with signed audit logs.</p>
              </div>
            </div>
          </Section>

          <Section icon={FileText} title="LHDN MyInvois Compliance">
            <p className="text-slate-600 leading-relaxed mb-3">StayMii e-Invoice supports businesses in preparing and submitting electronic invoices through the Malaysian MyInvois framework. The system allows businesses to:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1 ml-2">
              <li>Generate compliant invoice data</li>
              <li>Maintain invoice transaction records</li>
              <li>Integrate billing from StayMii Lite into e-Invoice workflows</li>
              <li>Support business compliance with Malaysian tax regulations</li>
            </ul>
            <p className="text-slate-600 mt-3 text-sm">StayMii provides the software infrastructure required for electronic invoicing, while the responsibility for legal tax compliance remains with the business issuing the invoice.</p>
          </Section>

          <Section icon={Lock} title="Data Security">
            <p className="text-slate-600 leading-relaxed mb-3">StayMii implements multiple layers of security designed to protect customer and guest data:</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                'Encrypted data transmission (HTTPS / TLS)',
                'Secure authentication controls',
                'Role-based user permissions',
                'System activity logging',
                'Infrastructure monitoring'
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 bg-slate-50 rounded-lg p-3">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                  <span className="text-slate-600 text-sm">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-slate-600 mt-3 text-sm">Sensitive information is protected using industry-standard security practices.</p>
          </Section>

          <Section icon={Server} title="Cloud Infrastructure">
            <p className="text-slate-600 leading-relaxed mb-3">StayMii operates on secure cloud infrastructure designed for high availability and scalability:</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                'Redundant server architecture',
                'Network protection mechanisms',
                'Backup and disaster recovery capabilities',
                'Continuous infrastructure monitoring'
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 bg-slate-50 rounded-lg p-3">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                  <span className="text-slate-600 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </Section>

          <Section icon={Eye} title="Data Privacy">
            <p className="text-slate-600 leading-relaxed mb-3">StayMii is designed to support compliance with relevant data protection regulations including Malaysia's Personal Data Protection Act (PDPA). The platform ensures:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1 ml-2">
              <li>Secure handling of guest information</li>
              <li>Controlled access to business data</li>
              <li>Data processing only for legitimate service purposes</li>
            </ul>
            <p className="text-slate-600 mt-3 text-sm">Hospitality businesses using StayMii remain responsible for how they collect and manage guest data within the system. For more information, please refer to our <Link to="/privacy-policy" className="text-[#2563EB] underline hover:text-[#1D4ED8]">Privacy Policy</Link>.</p>
          </Section>

          <Section icon={RefreshCw} title="Third-Party Integrations">
            <p className="text-slate-600 leading-relaxed mb-3">StayMii integrates with external hospitality platforms to support distribution and operational efficiency, including:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1 ml-2">
              <li>Online travel agencies like Booking.com and Agoda</li>
              <li>Channel managers</li>
              <li>Accounting software</li>
              <li>Payment gateway providers</li>
            </ul>
            <p className="text-slate-600 mt-2 text-sm">Each third-party service operates independently and may have its own security policies and compliance standards.</p>
          </Section>

          <Section icon={Users} title="Access Control">
            <p className="text-slate-600 leading-relaxed mb-3">StayMii uses role-based access controls to ensure that only authorized users can access sensitive data:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1 ml-2">
              <li>Administrator access</li>
              <li>Front office staff roles</li>
              <li>Finance roles</li>
              <li>Operational staff permissions</li>
            </ul>
          </Section>

          <Section icon={Bell} title="System Monitoring">
            <p className="text-slate-600 leading-relaxed mb-3">StayMii monitors system performance and security activity to detect potential issues:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1 ml-2">
              <li>System performance tracking</li>
              <li>Log monitoring</li>
              <li>Infrastructure alerts</li>
              <li>Security anomaly detection</li>
            </ul>
          </Section>

          <Section icon={Server} title="Business Continuity">
            <p className="text-slate-600 leading-relaxed mb-3">StayMii is designed to support business continuity through:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1 ml-2">
              <li>Cloud infrastructure redundancy</li>
              <li>Regular data backups</li>
              <li>Platform monitoring and maintenance</li>
            </ul>
          </Section>

          <Section icon={Users} title="Responsible Use">
            <p className="text-slate-600 leading-relaxed mb-3">Customers using StayMii are responsible for:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1 ml-2">
              <li>Protecting their login credentials</li>
              <li>Managing authorized staff access</li>
              <li>Ensuring accurate guest and billing information</li>
              <li>Complying with applicable legal and tax requirements</li>
            </ul>
          </Section>

          <Section icon={Building2} title="Contact Information">
            <p className="text-slate-600 leading-relaxed mb-3">For questions regarding StayMii security or compliance:</p>
            <div className="bg-slate-50 rounded-lg p-4 text-slate-600 text-sm">
              <p className="font-semibold text-slate-800">Mii Global Partner Holdings Sdn Bhd</p>
              <p>Registration No: 202601028203</p>
              <p>11, Taman Bukit Dumbar, 11600 Jelutong, Penang, Malaysia</p>
              <p className="mt-2">Email: <a href="mailto:support@staymii.com" className="text-[#2563EB] hover:underline">support@staymii.com</a></p>
              <p>Website: <a href="https://staymii.com" className="text-[#2563EB] hover:underline">https://staymii.com</a></p>
            </div>
          </Section>
        </div>
      </div>

      <LandingFooter />
    </div>
  );
};

export default SecurityCompliance;
